// Offline outbox storage for Pilot v1 offline log syncing
// Uses IndexedDB to store failed daily log submissions locally

export interface OfflineDailyLogEntry {
  id: string; // client-generated UUID
  createdAt: string; // ISO string
  wellId: number;
  logDate: string;
  logTime?: string;
  operatorInitial?: string;
  meterReadStart?: string;
  meterReadEnd?: string;
  gallonsPumped?: string;
  gpm?: string;
  hours?: string;
  remarks?: string;
  status: "draft" | "submitted";
  extraJson?: Record<string, any>;
  editReason?: string;
  lastError?: string;
  attempts: number;
  lastAttemptAt?: string;
}

export interface OutboxCounts {
  pending: number;
  failed: number;
}

// Initialize IndexedDB database
let dbPromise: Promise<IDBDatabase> | null = null;

const getDB = (): Promise<IDBDatabase> => {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open('city-well-outbox-v1', 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('dailyLogs')) {
          const store = db.createObjectStore('dailyLogs', { keyPath: 'id' });
          store.createIndex('createdAt', 'createdAt');
          store.createIndex('attempts', 'attempts');
          store.createIndex('lastAttemptAt', 'lastAttemptAt');
        }
      };
    });
  }
  return dbPromise;
};

// Generate client-side UUID for entries
const generateId = (): string => {
  return 'offline-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
};

// Queue a daily log entry for offline saving
export const enqueueDailyLog = async (entry: Omit<OfflineDailyLogEntry, 'id' | 'createdAt' | 'attempts'>): Promise<void> => {
  try {
    const db = await getDB();
    const offlineEntry: OfflineDailyLogEntry = {
      ...entry,
      id: generateId(),
      createdAt: new Date().toISOString(),
      attempts: 0,
    };

    const transaction = db.transaction(['dailyLogs'], 'readwrite');
    const store = transaction.objectStore('dailyLogs');
    await new Promise<void>((resolve, reject) => {
      const request = store.add(offlineEntry);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Failed to enqueue daily log:', error);
    throw error;
  }
};

// Get all offline entries that haven't been synced
export const listPendingDailyLogs = async (): Promise<OfflineDailyLogEntry[]> => {
  try {
    const db = await getDB();
    const transaction = db.transaction(['dailyLogs'], 'readonly');
    const store = transaction.objectStore('dailyLogs');

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Failed to list pending logs:', error);
    throw error;
  }
};

// Mark an entry as successfully synced
export const markDailyLogSynced = async (id: string): Promise<void> => {
  try {
    const db = await getDB();
    const transaction = db.transaction(['dailyLogs'], 'readwrite');
    const store = transaction.objectStore('dailyLogs');

    // First get the entry to confirm it exists
    const entry = await new Promise<OfflineDailyLogEntry>((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result);
        } else {
          reject(new Error('Entry not found'));
        }
      };
      request.onerror = () => reject(request.error);
    });

    // Remove the entry since it's been synced
    await new Promise<void>((resolve, reject) => {
      const deleteRequest = store.delete(id);
      deleteRequest.onsuccess = () => resolve();
      deleteRequest.onerror = () => reject(deleteRequest.error);
    });
  } catch (error) {
    console.error('Failed to mark daily log synced:', error);
    throw error;
  }
};

// Mark an entry as permanently failed
export const markDailyLogFailed = async (id: string, reason: string): Promise<void> => {
  try {
    const db = await getDB();
    const transaction = db.transaction(['dailyLogs'], 'readwrite');
    const store = transaction.objectStore('dailyLogs');

    // Get current entry
    const entry = await new Promise<OfflineDailyLogEntry>((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result);
        } else {
          reject(new Error('Entry not found'));
        }
      };
      request.onerror = () => reject(request.error);
    });

    // Update with failure information
    const updatedEntry: OfflineDailyLogEntry = {
      ...entry,
      lastError: reason,
      attempts: entry.attempts + 1,
      lastAttemptAt: new Date().toISOString(),
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.put(updatedEntry);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Failed to mark daily log failed:', error);
    throw error;
  }
};

// Get count of pending vs failed entries
export const getOutboxCounts = async (): Promise<OutboxCounts> => {
  try {
    const entries = await listPendingDailyLogs();
    const pending = entries.filter(entry => !entry.lastError).length;
    const failed = entries.filter(entry => entry.lastError).length;
    return { pending, failed };
  } catch (error) {
    console.error('Failed to get outbox counts:', error);
    return { pending: 0, failed: 0 };
  }
};
